// Angular
import { Component, OnInit, OnDestroy, ViewChild, AfterViewInit } from "@angular/core";

import { OrderTypeRenderer } from "../bfx-blotter-orders/renderers/OrderTypeRenderer";
import { SpotTrackingRenderer } from "../bfx-blotter-orders/renderers/SpotTrackingRenderer";
import { MarketMidRenderer } from "../bfx-blotter-orders/renderers/MarketMidRenderer";
import { PercentAwayRenderer } from "../bfx-blotter-orders/renderers/PercentAwayRenderer";
import { BfxHedgeOrderStagingGridComponent } from "./bfx-hedge-order-staging-grid.component";
import { RowNode } from "ag-grid-community";
import { Subscription } from "rxjs";
import { BmarkFixingData, BmarkFixingType } from "shared/cubiq-services";
import { LoggerService } from "@bfx/logger";
import { CommonIntentRenderer } from "app/bfx-blotter-orders/renderers/CommonIntentRenderer";
const logger = LoggerService.for(__dirname);

// Constants
export const DEFAULT_ROW_HEIGHT: number = 29;

@Component({
  selector: "bfx-hedge-order-staging-panel",
  templateUrl: "./html/bfx-hedge-order-staging-panel.component.html",
  standalone: false,
})
export class BfxHedgeOrderStagingPanelComponent implements OnInit, AfterViewInit, OnDestroy {
  // Grid definition
  public blotterId: string;
  public rowHeight = DEFAULT_ROW_HEIGHT;
  public columnsConfiguration: any = {};
  public sortingModel: any = null;
  public newDisabled: boolean = true;
  public copyDisabled: boolean = true;
  public deleteDisabled: boolean = true;
  public submitDisabled: boolean = true;
  private fixingData: BmarkFixingData = null;

  private selectionChangedSubscription: Subscription = null;

  public data: {
    button_labels: {
      new: string;
      copy: string;
      delete: string;
      submit: string;
      bulk_edit: string;
    };
    bulk_edit_value: boolean;
  } = {
    button_labels: {
      new: "hedge_order_monitor.button.new", // New
      copy: "hedge_order_monitor.button.copy", // Copy
      delete: "hedge_order_monitor.button.delete", // Delete
      submit: "hedge_order_monitor.button.submit", // Submit
      bulk_edit: "hedge_order_monitor.button.bulk_edit", // Enable Bulk Edit
    },
    bulk_edit_value: false,
  };

  public frameworkComponents: any = {
    orderTypeRenderer: OrderTypeRenderer,
    intentRenderer: CommonIntentRenderer, // Unused I think
    spotTrackingRenderer: SpotTrackingRenderer,
    percentAwayRenderer: PercentAwayRenderer,
    marketMidRenderer: MarketMidRenderer,
  };

  @ViewChild("stagingGrid") stagingGrid: BfxHedgeOrderStagingGridComponent;

  constructor() {}

  ngOnInit() {
    logger.out.info("BfxHedgeOrderStagingPanelComponent ::: initialized!");
    this.getComponentConfiguration();
  }

  ngAfterViewInit() {
    this.selectionChangedSubscription = this.stagingGrid.selectionChangedSubject.subscribe(
      this.gridSelectionChanged.bind(this),
    );
  }

  ngOnDestroy() {
    try {
      if (this.selectionChangedSubscription != null && !this.selectionChangedSubscription.closed) {
        this.selectionChangedSubscription.unsubscribe();
      }
    } catch (error) {
      logger.out.warn("Failed to cleanup hedge order monitor staging panel. " + error);
    }
  }

  public initFixingData(fixingData: BmarkFixingData): void {
    if (this.fixingData == null) {
      this.fixingData = fixingData;
      this.newDisabled = fixingData.type == BmarkFixingType.RECENT;
    }
  }

  protected gridSelectionChanged(nodes: RowNode[]): void {
    this.copyDisabled = nodes == null || nodes.length == 0;
    this.deleteDisabled =
      this.copyDisabled || nodes.find((node: RowNode) => node.data.processing) != null;
    this.submitDisabled =
      this.copyDisabled || nodes.find((node: RowNode) => !node.data.processing) == null;
  }

  public gridLayoutChange(event: any): void {
    if (this.stagingGrid) {
      this.updateConfig(this.stagingGrid);
    }
  }

  public getComponentConfiguration(): any {
    return {
      blotterId: this.blotterId,
      columnsConfiguration: this.columnsConfiguration,
      sortingModel: this.sortingModel,
    };
  }

  private updateConfig(grid: BfxHedgeOrderStagingGridComponent): void {
    this.blotterId = grid.blotterId;
    this.columnsConfiguration = grid.getBlotterColumnsConfigs();
    this.sortingModel = grid.getBlotterSortingModel();
  }

  public restoreFromConfiguration(config: any): void {
    this.columnsConfiguration = config.columnsConfiguration;
    this.sortingModel = config.sortingModel;
  }

  public newOrder(): void {
    this.stagingGrid.createNewOrder();
  }

  public copy(): void {
    this.stagingGrid.copySelected();
  }

  public delete(): void {
    this.stagingGrid.deleteSelected();
  }

  public submit(): void {
    this.stagingGrid.submitSelected();
  }

  public onBulkEditChange(value: boolean = null): void {
    if (value != null) {
      this.data.bulk_edit_value = value;
    }

    if (this.stagingGrid != null) {
      if (this.data.bulk_edit_value) {
        this.stagingGrid.addBulkEditRow();
      } else {
        this.stagingGrid.removeBulkEditRow();
      }
    }
  }

  public onStagingGridReady(): void {
    if (this.data.bulk_edit_value === true) {
      this.onBulkEditChange();
    }
  }
}
